import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, KPI, Card, Button, Input, Select, Label, Textarea, Empty } from "@/components/Primitives";
import { StatusPill, invoiceStatusVariant, ticketStatusVariant } from "@/components/StatusPill";
import { Search, Download, Pencil, X, AlertTriangle, Trash2, Ticket } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function gbp(v) { return `£${(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

export default function InvoicesPage({ scope = "admin" }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/invoices");
    // Hide drafts from the main list — they're noisy and not real outstanding revenue
    setItems((data || []).filter((i) => i.status !== "draft"));
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((i) => {
    if (status !== "All" && i.status !== status) return false;
    if (q && !`${i.number} ${i.customer_name}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const totalPaid = items.filter((i) => i.status === "paid").reduce((s, i) => s + i.amount, 0);
  const outstanding = items.filter((i) => ["pending", "overdue"].includes(i.status)).reduce((s, i) => s + i.amount, 0);
  const overdue = items.filter((i) => i.status === "overdue").reduce((s, i) => s + i.amount, 0);

  return (
    <div className="space-y-6">
      <PageHeader breadcrumb="Finance · Invoices" title="Invoices" subtitle="Synced from Stripe webhooks." />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <KPI label="Paid" value={gbp(totalPaid)} variant="success" />
        <KPI label="Outstanding" value={gbp(outstanding)} variant={outstanding > 0 ? "warning" : "default"} />
        <KPI label="Overdue" value={gbp(overdue)} variant={overdue > 0 ? "danger" : "success"} />
      </div>
      <Card>
        <div className="px-5 py-3 flex flex-wrap items-center gap-3 border-b border-[hsl(var(--border))]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.6}/>
            <Input placeholder="Search by number / customer…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 w-[280px]"/>
          </div>
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-[160px]">
            <option>All</option><option>paid</option><option>pending</option><option>overdue</option><option>draft</option>
          </Select>
          <div className="ml-auto text-[12px] text-[hsl(var(--text-muted))]">{filtered.length} results</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))]">
                <th className="text-left font-semibold px-5 py-3">Number</th>
                {scope === "admin" && <th className="text-left font-semibold px-5 py-3">Customer</th>}
                <th className="text-left font-semibold px-5 py-3">Status</th>
                <th className="text-left font-semibold px-5 py-3">Issued</th>
                <th className="text-left font-semibold px-5 py-3">Due</th>
                <th className="text-right font-semibold px-5 py-3">Amount</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => (
                <tr key={i.id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-hover))]" data-testid="invoice-row">
                  <td className="px-5 py-3 font-mono font-semibold">
                    {i.number}
                    {i.project_id && <div className="text-[10px] font-sans font-normal text-[hsl(var(--primary))] uppercase tracking-widest mt-0.5">· Project linked</div>}
                  </td>
                  {scope === "admin" && <td className="px-5 py-3">{i.customer_name}</td>}
                  <td className="px-5 py-3"><StatusPill variant={invoiceStatusVariant(i.status)}>{i.status}</StatusPill></td>
                  <td className="px-5 py-3 text-[hsl(var(--text-muted))]">{new Date(i.issued_date).toLocaleDateString()}</td>
                  <td className="px-5 py-3 text-[hsl(var(--text-muted))]">{new Date(i.due_date).toLocaleDateString()}</td>
                  <td className="px-5 py-3 text-right font-mono font-semibold">{gbp(i.amount)}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      {scope === "admin" && (
                        <Button size="sm" variant="ghost" onClick={() => setEditing(i)} data-testid="edit-invoice-btn" title="Edit">
                          <Pencil className="w-3.5 h-3.5" strokeWidth={1.6}/>
                        </Button>
                      )}
                      {i.hosted_invoice_url ? (
                        <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer">
                          <Button size="sm" variant={i.status === "paid" ? "ghost" : "primary"}>
                            <Download className="w-3.5 h-3.5" strokeWidth={1.6}/>{i.status === "paid" ? "Receipt" : "Pay / View"}
                          </Button>
                        </a>
                      ) : i.invoice_pdf ? (
                        <a href={i.invoice_pdf} target="_blank" rel="noreferrer">
                          <Button size="sm" variant="ghost"><Download className="w-3.5 h-3.5" strokeWidth={1.6}/>PDF</Button>
                        </a>
                      ) : (
                        <Button size="sm" variant="ghost"><Download className="w-3.5 h-3.5" strokeWidth={1.6}/>PDF</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={7}><Empty title="No invoices"/></td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {editing && (
        <InvoiceEditModal
          invoice={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function InvoiceEditModal({ invoice, onClose, onSaved }) {
  const nav = useNavigate();
  const [form, setForm] = useState({
    status: invoice.status || "pending",
    amount: invoice.amount ?? 0,
    amount_paid: invoice.amount_paid ?? 0,
    currency: invoice.currency || "gbp",
    issued_date: (invoice.issued_date || "").split("T")[0],
    due_date: (invoice.due_date || "").split("T")[0],
    number: invoice.number || "",
    project_id: invoice.project_id || "",
  });
  const [projects, setProjects] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fromStripe = !!invoice.stripe_invoice_id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data || []));
    api.get("/tickets", { params: { invoice_id: invoice.id } })
      .then(({ data }) => setTickets(data || []))
      .catch(() => {});
  }, [invoice.id]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount) || 0,
        amount_paid: parseFloat(form.amount_paid) || 0,
        project_id: form.project_id || null,
        issued_date: form.issued_date ? new Date(form.issued_date).toISOString() : invoice.issued_date,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : invoice.due_date,
      };
      await api.patch(`/invoices/${invoice.id}`, payload);
      toast.success("Invoice updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const onDelete = async () => {
    const msg = fromStripe
      ? `Delete invoice ${invoice.number} from Soubor One & Firestore? NOTE: the invoice still exists in Stripe. It will be re-imported on the next sync.`
      : `Delete invoice ${invoice.number}? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      await api.delete(`/invoices/${invoice.id}`);
      toast.success("Invoice deleted");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    } finally { setDeleting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-lg rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl" data-testid="invoice-edit-modal">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <h3 className="text-lg font-semibold">Edit invoice {invoice.number}</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
        </div>

        <div className="p-6 space-y-4">
          {fromStripe && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 flex items-start gap-2 text-[12px]">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" strokeWidth={2}/>
              <div>
                This invoice is synced from Stripe. Edits here may be overwritten when Stripe sends the next update for this invoice. For permanent changes, edit it in Stripe.
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div><Label>Number</Label><Input value={form.number} onChange={(e) => set("number", e.target.value)}/></div>
            <div><Label>Status</Label>
              <Select value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="draft">Draft</option>
                <option value="pending">Pending</option>
                <option value="overdue">Overdue</option>
                <option value="paid">Paid</option>
                <option value="void">Void</option>
              </Select>
            </div>
            <div><Label>Amount</Label><Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="invoice-amount"/></div>
            <div><Label>Amount paid</Label><Input type="number" min="0" step="0.01" value={form.amount_paid} onChange={(e) => set("amount_paid", e.target.value)}/></div>
            <div><Label>Issued date</Label><Input type="date" value={form.issued_date} onChange={(e) => set("issued_date", e.target.value)}/></div>
            <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="invoice-due-date"/></div>
            <div className="col-span-2">
              <Label>Assign to project</Label>
              <Select value={form.project_id} onChange={(e) => set("project_id", e.target.value)} data-testid="invoice-project-select">
                <option value="">(Unallocated)</option>
                {(() => {
                  const same = projects.filter((p) => invoice.customer_id && p.customer_id === invoice.customer_id);
                  const other = projects.filter((p) => !invoice.customer_id || p.customer_id !== invoice.customer_id);
                  return (
                    <>
                      {same.length > 0 && <optgroup label={`Same customer · ${invoice.customer_name || ""}`}>
                        {same.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </optgroup>}
                      {other.length > 0 && <optgroup label="Other projects">
                        {other.map((p) => <option key={p.id} value={p.id}>{p.name}{p.customer_name ? ` · ${p.customer_name}` : ""}</option>)}
                      </optgroup>}
                    </>
                  );
                })()}
              </Select>
              <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1">This invoice's value will be counted against the project's budget (fixed-price) or total invoiced (ongoing).</div>
            </div>
          </div>

          {tickets.length > 0 && (
            <div className="pt-2" data-testid="invoice-linked-tickets">
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold mb-2 inline-flex items-center gap-1.5">
                <Ticket className="w-3 h-3" strokeWidth={2}/>Linked tickets ({tickets.length})
              </div>
              <div className="rounded-lg border border-[hsl(var(--border))] divide-y divide-[hsl(var(--border))]">
                {tickets.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => { onClose(); nav("/tickets"); }}
                    className="w-full text-left px-3 py-2 hover:bg-[hsl(var(--surface-hover))] flex items-center gap-3"
                    data-testid="invoice-ticket-row"
                  >
                    <Ticket className={`w-3.5 h-3.5 shrink-0 ${t.internal ? "text-amber-600 dark:text-amber-400" : "text-[hsl(var(--primary))]"}`} strokeWidth={1.8}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-semibold truncate">{t.subject}</div>
                      <div className="text-[10.5px] text-[hsl(var(--text-muted))]">
                        {t.number}
                        {t.internal && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 text-[9px] uppercase tracking-widest font-bold">Internal</span>}
                      </div>
                    </div>
                    <StatusPill variant={ticketStatusVariant(t.status)}>{t.status.replace("_", " ")}</StatusPill>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-between gap-2">
          <Button type="button" variant="ghost" onClick={onDelete} disabled={deleting} className="text-rose-600 hover:text-rose-700" data-testid="invoice-delete-btn">
            <Trash2 className="w-4 h-4" strokeWidth={1.6}/>{deleting ? "Deleting…" : "Delete"}
          </Button>
          <div className="flex items-center gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="invoice-save-btn">{saving ? "Saving…" : "Save"}</Button>
          </div>
        </div>
      </form>
    </div>
  );
}
